import { Scenario } from '@/types';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, catchError, isObservable, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'sm-dac-scenario-infos',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dac-scenario-infos.component.html',
  styleUrls: ['./dac-scenario-infos.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DacScenarioInfosComponent {
  private scenarioDataSubject$ = new BehaviorSubject<Observable<Scenario | null | undefined>>(of(null));
  private selectedEtapeSubject$ = new BehaviorSubject<Observable<string>>(of(''));

  @Input()
  set selectedEtape(value: Observable<string> | string | undefined) {
    if (isObservable(value)) {
      this.selectedEtapeSubject$.next(
        value.pipe(
          catchError(error => {
            console.error('DacScenarioInfosComponent: Error in selectedEtape observable', error);
            return of('');
          })
        )
      );
      return;
    }
    this.selectedEtapeSubject$.next(of(value ?? ''));
  }

  @Input() onEtapeSelect?: (etapeCode: string) => void;

  selectEtape(etapeCode: string): void {
    if (this.onEtapeSelect) {
      this.onEtapeSelect(etapeCode);
    }
  }

  @Input()
  set scenarioData(value: Observable<Scenario | null | undefined> | Scenario | null | undefined) {
    if (isObservable(value)) {
      this.scenarioDataSubject$.next(
        value.pipe(
          catchError(error => {
            console.error('DacScenarioInfosComponent: Error in scenarioData observable', error);
            return of(null);
          })
        )
      );
      return;
    }

    this.scenarioDataSubject$.next(of(value));
  }

  readonly scenarioViewModel$ = combineLatest([
    this.scenarioDataSubject$.pipe(switchMap(obs => obs)),
    this.selectedEtapeSubject$.pipe(switchMap(obs => obs))
  ]).pipe(
    map(([scenario, selectedEtape]) => {
      if (!scenario) return null;

      const stepsMap = new Map<
        number,
        {
          id: number;
          code: string;
          name: string;
          folders: {
            id: number;
            code: string;
            label: string;
            order: number;
          }[];
        }
      >();

      (scenario.scenarioEtapes ?? []).forEach(step => {
        const folder = {
          id: step.typeDossierId,
          code: step.typeDossierCode,
          label: step.typeDossierLibelle,
          order: step.orderEvaluation,
        };

        const existingStep = stepsMap.get(step.etapeId);

        if (existingStep) {
          existingStep.folders = [...existingStep.folders, folder].sort((a, b) => a.order - b.order);
          return;
        }

        stepsMap.set(step.etapeId, {
          id: step.etapeId,
          code: step.etapeCode,
          name: step.etapeNom,
          folders: [folder],
        });
      });

      return {
        code: scenario.code,
        name: scenario.nom,
        description: scenario.description?.trim() || 'Aucune description disponible pour ce scenario.',
        active: Boolean(scenario.active),
        modePassations: (scenario.modePassations ?? []).map(m => m.libelle),
        steps: Array.from(stepsMap.values()),
        selectedEtape
      };
    })
  );
}
